import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ZileeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: "Hi! I'm Zilee, your Spee'V'Dee assistant. Ask me anything about our delivery service, pricing, safety, or how it works!" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2, 10));
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/marketing/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'bot', text: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', text: "Sorry, I couldn't connect. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="zilee-chat-btn"
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            width: 60, height: 60, borderRadius: '50%',
            background: 'linear-gradient(135deg, #7851A9, #5B2D8E)',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(120,81,169,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <MessageCircle size={26} color="#fff" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div data-testid="zilee-chat-window" style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 370, maxWidth: 'calc(100vw - 32px)',
          height: 520, maxHeight: 'calc(100vh - 100px)',
          borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column',
          background: '#fff',
          border: '1px solid rgba(120,81,169,0.15)',
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #7851A9, #5B2D8E)',
            padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <MessageCircle size={18} color="#fff" />
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Zilee</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>Spee'V'Dee Support</div>
              </div>
            </div>
            <button
              data-testid="zilee-close-btn"
              onClick={() => setOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                width: 30, height: 30, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={16} color="#fff" />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px 14px',
            display: 'flex', flexDirection: 'column', gap: 10,
            background: '#f9fafb',
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '80%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: msg.role === 'user' ? 'linear-gradient(135deg, #7851A9, #5B2D8E)' : '#fff',
                  color: msg.role === 'user' ? '#fff' : '#374151',
                  fontSize: 13, lineHeight: 1.5,
                  boxShadow: msg.role === 'bot' ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: '14px 14px 14px 4px',
                  background: '#fff', fontSize: 13, color: '#9CA3AF',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                  Zilee is typing...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 14px', borderTop: '1px solid #E5E7EB',
            display: 'flex', gap: 8, background: '#fff',
          }}>
            <input
              data-testid="zilee-input"
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Ask Zilee anything..."
              style={{
                flex: 1, border: '1px solid #E5E7EB', borderRadius: 10,
                padding: '10px 14px', fontSize: 13, outline: 'none',
              }}
            />
            <button
              data-testid="zilee-send-btn"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: input.trim() ? 'linear-gradient(135deg, #7851A9, #5B2D8E)' : '#E5E7EB',
                border: 'none', cursor: input.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s',
              }}
            >
              <Send size={16} color={input.trim() ? '#fff' : '#9CA3AF'} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ZileeChat;
